import { ParsedUrlQuery } from "querystring";

import { parseRelativePath } from "@/common/utils/url.utils";

/**
 * This function cleans the search params from the URLSearchParams object.
 * It removes all the search params that NextJS adds for some reason to asPath
 * which we don't want.
 *
 * It mutates the URLSearchParams object and returns it.
 *
 * @param {URLSearchParams} searchParams - URLSearchParams object
 * @returns {URLSearchParams} - URLSearchParams object
 */
export const cleanSearchParams = (searchParams: URLSearchParams) => {
  // Catch-all pages leak their segment name (and its 'nxtP'-prefixed twin) into the url
  // as a search param. This is weird bug/behaviour in nextjs, which won't be fixed
  // anything soon. So we remove it here. 'search' is pages/[...search].tsx; 'search2'
  // is what that catch-all was called while the two search UIs ran side by side, still
  // in cached asPaths.
  for (const param of ["search", "search2"]) {
    searchParams.delete(param);
    searchParams.delete(`nxtP${param}`);
  }

  // Sometimes pages after getting redirected to correct locale has 'nextInternalLocale'
  // as search param in url. So we remove it here.
  searchParams.delete("nextInternalLocale");

  return searchParams;
};

/**
 * This function cleans the search params from the url.
 * It removes all the search params that NextJS adds for some reason to asPath
 * which we don't want.
 *
 * @param {string} url - The url string
 * @returns {string} - The cleaned url string
 */
export const cleanSearchParamsFromUrl = (url: string) => {
  const urlObject = parseRelativePath(url);

  cleanSearchParams(urlObject.searchParams);

  return `${urlObject.pathname}${urlObject.search}`;
};

/**
 * "Extracts" url parameter and converts it using provided converted function.
 *
 * @param query Routers query object.
 * @param paramName Name of the parameter to "extract".
 * @param converter Converter function.
 */
export const getURLParameter = <T>(
  query: ParsedUrlQuery,
  paramName: string,
  converter: (value: string) => T
) => {
  const maybeValue = query[paramName];
  const value = Array.isArray(maybeValue) ? maybeValue[0] : maybeValue;

  if (!value) {
    return;
  }

  return converter(value);
};
